// src/components/SimpleComponents/SimpleInputWithButton.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useRef } from 'react';
import { Input, Button, Space, Tooltip, Form } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { useSimpleFormContext } from './SimpleFormProvider';
import { useModuleContext } from '../core/ModuleProvider';
import translateX from 'utils/translateX';
import utils from 'utils/utils';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleInputWithButton = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  disabled = false,
  prefix,
  onBlur,

  // Button props
  buttonIcon,
  buttonType = 'primary',
  buttonStyle = {},
  onButtonClick,
  buttonTooltip,
  buttonDisabled,

  // Read-only mode (for write-only fields like CSC token)
  readOnly = false,

  useInitialInAvatar,
  useFirstInitialInAvatar,
  useSecondInitialInAvatar,

  repeatOnField,

  validateTrigger = 'onBlur',
  showFeedback = false,

  minLength,
  maxLength,

  placeholder,

  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1) CONTEXTS
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, form, disabledFields } = useSimpleFormContext();
  const { setState, avatarInitialFirst, avatarInitialSecond } = useModuleContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2) HOOKS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Watch form value - needed because Input inside Space.Compact doesn't receive
  // value injection from Form.Item (only direct children receive it)
  const watchedValue = Form.useWatch(fullFieldName, form);

  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const isButtonDisabled = buttonDisabled !== undefined ? buttonDisabled : locked;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const lastInitialsRef = useRef(null);
  const avatarDebounceTimerRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (avatarDebounceTimerRef.current) {
        clearTimeout(avatarDebounceTimerRef.current);
      }
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6) HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const internalOnChanges = useCallback(
    (ev, ...restArgs) => {
      if (useInitialInAvatar || useFirstInitialInAvatar || useSecondInitialInAvatar) {
        const value = ev?.target?.value ?? ev ?? '';

        if (avatarDebounceTimerRef.current) {
          clearTimeout(avatarDebounceTimerRef.current);
        }

        avatarDebounceTimerRef.current = setTimeout(() => {
          const newInitials = utils.getNameInitial(value);

          if (newInitials !== lastInitialsRef.current) {
            lastInitialsRef.current = newInitials;
            applyAvatarInitials({
              value,
              mode: useInitialInAvatar ? 'all' : useFirstInitialInAvatar ? 'first' : 'second',
              setState,
              avatarInitialFirst,
              avatarInitialSecond,
            });
          }
        }, 100);
      }

      if (props.onChange) props.onChange(ev, ...restArgs);
    },
    [
      useInitialInAvatar,
      useFirstInitialInAvatar,
      useSecondInitialInAvatar,
      setState,
      avatarInitialFirst,
      avatarInitialSecond,
      props.onChange,
    ]
  );

  const { customValidationStatus, customValidationMessage, handleChange } =
    useCustomValidationStatus({
      ...props,
      minLength,
      maxLength,
      onChange: internalOnChanges,
      ...elementProps,
    });

  const handleBlur = useCallback(
    ev => {
      const value = ev?.target?.value ?? ev ?? '';

      if (useInitialInAvatar || useFirstInitialInAvatar || useSecondInitialInAvatar) {
        if (avatarDebounceTimerRef.current) {
          clearTimeout(avatarDebounceTimerRef.current);
          avatarDebounceTimerRef.current = null;
        }

        const newInitials = utils.getNameInitial(value);
        if (newInitials !== lastInitialsRef.current) {
          lastInitialsRef.current = newInitials;
          applyAvatarInitials({
            value,
            mode: useInitialInAvatar ? 'all' : useFirstInitialInAvatar ? 'first' : 'second',
            setState,
            avatarInitialFirst,
            avatarInitialSecond,
          });
        }
      }

      setTimeout(() => {
        if (repeatOnField) {
          if (!form?.getFieldValue(repeatOnField)) {
            form?.setFieldValue(repeatOnField, value);
          }
        }

        onBlur?.(ev);
      }, 0);
    },
    [
      repeatOnField,
      form,
      onBlur,
      flattenFieldName,
      useInitialInAvatar,
      useFirstInitialInAvatar,
      useSecondInitialInAvatar,
      setState,
      avatarInitialFirst,
      avatarInitialSecond,
    ]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7) UI
  // ─────────────────────────────────────────────────────────────────────────────
  const buttonElement = (
    <Button
      type={buttonType}
      icon={buttonIcon}
      onClick={onButtonClick}
      disabled={isButtonDisabled}
      style={{ height: 40, ...buttonStyle }}
    />
  );

  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
      style={styleItem}
      validateTrigger={validateTrigger}
      showFeedback={showFeedback}
    >
      <Space.Compact style={{ width: '100%' }}>
        <Input
          ref={elementRef}
          id={flattenFieldName}
          style={{ height: 40, ...style }}
          value={watchedValue ?? ''}
          onBlur={handleBlur}
          onChange={handleChange}
          disabled={isDisabled}
          readOnly={readOnly}
          prefix={prefix}
          minLength={minLength}
          maxLength={maxLength}
          placeholder={translateX(placeholder)}
        />
        {buttonTooltip ? (
          <Tooltip title={translateX(buttonTooltip)}>{buttonElement}</Tooltip>
        ) : (
          buttonElement
        )}
      </Space.Compact>
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleInputWithButton;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function applyAvatarInitials({ value, mode, setState, avatarInitialFirst, avatarInitialSecond }) {
  if (!mode) return;

  const v = value ?? '';

  if (mode === 'all') {
    setState({ avatarInitial: v });
    return;
  }

  if (mode === 'first') {
    setState({
      avatarInitial: `${v} ${avatarInitialSecond || ''}`.trim(),
      avatarInitialFirst: v,
    });
    return;
  }

  if (mode === 'second') {
    setState({
      avatarInitial: `${avatarInitialFirst || ''} ${v}`.trim(),
      avatarInitialSecond: v,
    });
  }
}
